import { useEffect, useMemo, useState } from 'react';
import { isEmpty, isLoaded } from 'react-redux-firebase';

import { useSelector } from 'react-redux';
import { isViewingClientPortal } from '../shared/utilities';

const authIsValid = (auth, roles, clientId, uid) => {
  // If no auth config provided, allow access
  if (!auth) {
    return true;
  }

  // Check allowedUids first (highest priority)
  if (auth.allowedUids?.includes(uid)) {
    return true;
  }

  const userRole = roles[clientId] || '0';
  const asseticomRole = roles?.asseticom || '0';

  // Check denyList (blocks access regardless of other rules)
  if (auth.denyList) {
    if (
      auth.denyList.includes(userRole) ||
      auth.denyList.includes(asseticomRole)
    ) {
      return false;
    }
  }

  let hasAccess = false;

  // Check atLeast (minimum role requirement)
  if (auth.atLeast) {
    const roleIsAllowed =
      roles[clientId] &&
      parseInt(auth.atLeast, 10) <= parseInt(roles[clientId], 10);
    const asseticomRoleAllowed =
      roles?.asseticom &&
      parseInt(roles.asseticom, 10) >= parseInt(auth.atLeast, 10);

    hasAccess = !!(asseticomRoleAllowed || roleIsAllowed);
  }

  // Check allowList (additional roles allowed beyond atLeast)
  if (auth.allowList && auth.allowList.length > 0) {
    const isInAllowList =
      auth.allowList.includes(userRole) ||
      auth.allowList.includes(asseticomRole);
    hasAccess = hasAccess || isInAllowList;
  }

  // If only allowList is specified (no atLeast), use allowList as the sole criteria
  if (!auth.atLeast && auth.allowList && auth.allowList.length > 0) {
    hasAccess =
      auth.allowList.includes(userRole) ||
      auth.allowList.includes(asseticomRole);
  }

  // If neither atLeast nor allowList specified, default to true (allow access)
  if (!auth.atLeast && (!auth.allowList || auth.allowList.length === 0)) {
    hasAccess = true;
  }

  return hasAccess;
};

export default function useAuthChecker(auth, label) {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const roles = useSelector(
    (state) => state.firebase.profile.token.claims.roles,
  );
  const ids = useSelector((state) => state.params.ids);
  const uid = useSelector((state) => state.firebase.auth.uid);

  useEffect(() => {
    if (auth && !isChecking) {
      const valid = authIsValid(auth, roles, ids.clientId, uid);
      return setIsAuthed(valid);
    }
    return setIsAuthed(true);
  }, [auth, roles, ids, uid, isChecking]);

  useEffect(() => {
    const idsNotPopulated = isViewingClientPortal() && isEmpty(ids);
    const rolesNotLoaded = !isLoaded(roles);
    setIsChecking(idsNotPopulated || rolesNotLoaded);
  }, [roles, ids]);

  const noRoles = useMemo(() => {
    return isLoaded(roles) && isEmpty(roles);
  });

  const checkAuth = (toCheck) => {
    return !toCheck || authIsValid(toCheck, roles, ids.clientId, uid);
  };

  // console.log(`${label} auth:`, auth, { isAuthed }); // Debugging log to check auth config

  return { isAuthed, isChecking, checkAuth, noRoles };
}
